import React, { useEffect, useMemo, useState } from 'react';
import { Button, Spin, Tag, Typography } from 'antd';
import { CheckOutlined, CloseOutlined, CreditCardOutlined, LoginOutlined, ReloadOutlined } from '@ant-design/icons';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { AppState } from '../store';
import { confirmOrganizationCardTokenPayPhone, confirmPublicPayPhoneTrial } from '../store/user/thunks';
import './PayPhonePublic.css';
import bluemedixLogo from './assets/bluemedix-white.png';
import {
    getInitialPayPhoneLanguage,
    payPhoneLocale,
    payPhoneResponseText,
    PayPhoneLanguage,
    persistPayPhoneLanguage
} from './payPhonePublicLanguage';

const { Text, Title } = Typography;
const pendingPayPhoneStorageKey = 'tbc.payphone.pendingContractPayment';
const bridgeTokenStoragePrefix = 'tbc.payphone.bridgeToken.';
const bluemedixPortalUrl = 'https://bluemedix.thebluecoding.com/';
const isCardTokenTransaction = (clientTxId: string, pending?: any) =>
    pending?.mode === 'cardtoken'
    || pending?.type === 'cardtoken'
    || clientTxId.toUpperCase().startsWith('TBC-CARD-');

const money = (value: number, currency = 'USD', language: PayPhoneLanguage) =>
    new Intl.NumberFormat(payPhoneLocale(language), {
        style: 'currency',
        currency,
        minimumFractionDigits: 2
    }).format(value || 0);

const formatDate = (value: string | Date | null | undefined, language: PayPhoneLanguage, pendingText: string) => {
    if (!value) {
        return pendingText;
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return pendingText;
    }

    return new Intl.DateTimeFormat(payPhoneLocale(language), {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
    }).format(date);
};

const cleanSensitivePayPhoneQuery = (id: string | null, clientTransactionId: string | null) => {
    const cleanQuery = new URLSearchParams();

    if (id) {
        cleanQuery.set('id', id);
    }

    if (clientTransactionId) {
        cleanQuery.set('clientTransactionId', clientTransactionId);
    }

    const nextUrl = `${window.location.pathname}${cleanQuery.toString() ? `?${cleanQuery.toString()}` : ''}`;
    window.history.replaceState({}, document.title, nextUrl);
};

const PublicShell: React.FC<{
    children: React.ReactNode;
    language: PayPhoneLanguage;
    onLanguageChange: (language: PayPhoneLanguage) => void;
}> = ({ children, language, onLanguageChange }) => (
    <main className="bm-payphone-page">
        <div className="bm-payphone-shape bm-payphone-shape-one" />
        <div className="bm-payphone-shape bm-payphone-shape-two" />
        <div className="bm-payphone-shape bm-payphone-shape-three" />
        <section className="bm-payphone-container">
            <header className="bm-payphone-topbar">
                <img src={bluemedixLogo} alt="BlueMedix" className="bm-payphone-logo" />
                <div className="bm-payphone-language">
                    <button type="button" className={language === 'es' ? 'active' : ''} onClick={() => onLanguageChange('es')}>
                        ES
                    </button>
                    <button type="button" className={language === 'en' ? 'active' : ''} onClick={() => onLanguageChange('en')}>
                        EN
                    </button>
                </div>
            </header>
            {children}
        </section>
    </main>
);

const DetailTile: React.FC<{ label: string; value?: React.ReactNode; fallback: string }> = ({ label, value, fallback }) => (
    <div className="bm-payphone-detail">
        <span>{label}</span>
        <strong>{value || fallback}</strong>
    </div>
);

const MovementCard: React.FC<{
    title: string;
    subtitle: string;
    trailing: React.ReactNode;
    items: Array<{ label: string; value: React.ReactNode }>;
}> = ({ title, subtitle, trailing, items }) => (
    <article className="bm-payphone-movement">
        <div className="bm-payphone-movement-head">
            <div>
                <h3>{title}</h3>
                <p>{subtitle}</p>
            </div>
            <div className="bm-payphone-movement-trailing">{trailing}</div>
        </div>
        <div className="bm-payphone-kv-grid">
            {items.map((item) => (
                <div key={item.label} className="bm-payphone-kv">
                    <span>{item.label}</span>
                    <strong>{item.value}</strong>
                </div>
            ))}
        </div>
    </article>
);

const PayPhoneResponse: React.FC<any> = (props: any) => {
    const history = useHistory();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [result, setResult] = useState<any>(null);
    const [error, setError] = useState('');
    const [language, setLanguage] = useState<PayPhoneLanguage>(getInitialPayPhoneLanguage);

    const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const t = payPhoneResponseText[language];

    const handleLanguageChange = (nextLanguage: PayPhoneLanguage) => {
        setLanguage(nextLanguage);
        persistPayPhoneLanguage(nextLanguage);
    };

    useEffect(() => {
        document.documentElement.lang = language;
    }, [language]);

    useEffect(() => {
        const confirmPayment = async () => {
            const idParam = query.get('id');
            const clientTxId = query.get('clientTransactionId') || query.get('clientTxId') || '';
            const id = Number(idParam || 0);
            const cToken = query.get('ctoken') || query.get('cToken') || '';
            const pendingRaw = localStorage.getItem(pendingPayPhoneStorageKey);
            const storedBridgeToken = clientTxId
                ? localStorage.getItem(`${bridgeTokenStoragePrefix}${clientTxId}`)
                : null;

            if (cToken) {
                cleanSensitivePayPhoneQuery(idParam, clientTxId);
            }

            if (!id || !clientTxId) {
                setError(t.missingInfo);
                setLoading(false);
                return;
            }

            try {
                let response;

                if (pendingRaw) {
                    const pending = JSON.parse(pendingRaw);
                    if (pending.clientTransactionId && pending.clientTransactionId !== clientTxId) {
                        setError(t.transactionMismatch);
                        setLoading(false);
                        return;
                    }

                    response = isCardTokenTransaction(clientTxId, pending)
                        ? await props.confirmOrganizationCardTokenPayPhone({
                            id,
                            clientTxId,
                            cToken
                        })
                        : await props.confirmPublicPayPhoneTrial({
                            id,
                            clientTxId,
                            cToken,
                            organizationId: pending.organizationId,
                            planId: pending.planId,
                            currentContractId: pending.currentContractId || null,
                            bridgeToken: pending.bridgeToken || storedBridgeToken || null,
                            clientData: pending.clientData
                        });
                } else {
                    response = isCardTokenTransaction(clientTxId)
                        ? await props.confirmOrganizationCardTokenPayPhone({
                            id,
                            clientTxId,
                            cToken
                        })
                        : await props.confirmPublicPayPhoneTrial({
                            id,
                            clientTxId,
                            cToken,
                            bridgeToken: storedBridgeToken || null
                        });
                }

                if (response?.payPhone?.isApproved) {
                    localStorage.removeItem(pendingPayPhoneStorageKey);
                    localStorage.removeItem(`${bridgeTokenStoragePrefix}${clientTxId}`);
                }

                setResult(response);
            } catch {
                setError(t.confirmError);
            } finally {
                setLoading(false);
            }
        };

        confirmPayment();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query]);

    if (loading) {
        return (
            <PublicShell language={language} onLanguageChange={handleLanguageChange}>
                <section className="bm-payphone-glass bm-payphone-state-card">
                    <Spin size="large" />
                    <Title level={3}>{t.confirmingTitle}</Title>
                    <Text>{t.confirmingText}</Text>
                </section>
            </PublicShell>
        );
    }

    if (error || !result?.payPhone?.isApproved) {
        return (
            <PublicShell language={language} onLanguageChange={handleLanguageChange}>
                <section className="bm-payphone-glass bm-payphone-state-card">
                    <div className="bm-payphone-error-icon">
                        <CloseOutlined />
                    </div>
                    <Title level={3}>{t.errorTitle}</Title>
                    <Text>{error || result?.payPhone?.message || t.notApproved}</Text>
                    <div className="bm-payphone-actions">
                        <Button onClick={() => history.push('/plan-billing')}>{t.back}</Button>
                        <Button type="primary" icon={<ReloadOutlined />} onClick={() => window.location.reload()}>
                            {t.retry}
                        </Button>
                    </div>
                </section>
            </PublicShell>
        );
    }

    const generatedContract = result.generatedContract || result.trialContract;
    const cardToken = result.cardToken || {};
    const payPhone = result.payPhone || {};
    const currency = cardToken.validationCurrency || payPhone.currency || 'USD';
    const validationAmount = Number(cardToken.validationAmount || Number(payPhone.amount || 0) / 100 || 0);
    const reverseSucceeded = cardToken.reverseStatus === 'REVERSADO' || result.reverse?.success;
    const customerName = cardToken.cardHolderName || payPhone.cardHolderName || 'Cliente';
    const validationDate = cardToken.validationPaidAt || payPhone.date || payPhone.transactionDate;
    const reverseDate = cardToken.reverseAt;

    return (
        <PublicShell language={language} onLanguageChange={handleLanguageChange}>
            <section className="bm-payphone-voucher">
                <div className="bm-payphone-voucher-header">
                    <div className="bm-payphone-titlemark">
                        <CreditCardOutlined />
                    </div>
                    <h1>{t.voucherTitle}</h1>
                    <Tag color="success">{t.approved}</Tag>
                </div>

                <div className="bm-payphone-voucher-body">
                    <div className="bm-payphone-success-icon">
                        <CheckOutlined />
                    </div>
                    <h2>{t.completed}</h2>
                    <p className="bm-payphone-greeting">{t.hello}, {customerName}</p>
                    <p className="bm-payphone-muted">
                        {t.successText}
                    </p>

                    <div className="bm-payphone-movement-grid">
                        <MovementCard
                            title={t.validationTitle}
                            subtitle={t.validationSubtitle}
                            trailing={money(validationAmount, currency, language)}
                            items={[
                                { label: t.date, value: formatDate(validationDate, language, t.pending) },
                                { label: t.status, value: <Tag color="green">{payPhone.transactionStatus || 'Approved'}</Tag> },
                                { label: t.transaction, value: payPhone.transactionId || cardToken.validationTransactionId || t.notRegistered },
                                { label: t.authorization, value: payPhone.authorizationCode || cardToken.validationAuthorizationCode || t.notRegistered }
                            ]}
                        />

                        <MovementCard
                            title={t.reverseTitle}
                            subtitle={t.reverseSubtitle}
                            trailing={<Tag color={reverseSucceeded ? 'green' : 'gold'}>{cardToken.reverseStatus || (reverseSucceeded ? t.reversed : t.reversePending)}</Tag>}
                            items={[
                                { label: t.reverseDate, value: formatDate(reverseDate, language, t.pending) },
                                { label: t.reversedAmount, value: reverseSucceeded ? money(validationAmount, currency, language) : t.pending },
                                { label: t.reverseTransaction, value: cardToken.reverseTransactionId || result.reverse?.transactionId || t.notRegistered },
                                { label: t.tokenization, value: t.tokenSaved }
                            ]}
                        />
                    </div>

                    <div className="bm-payphone-detail-grid">
                        <DetailTile label={t.organization} value={cardToken.organizationName || t.notRegistered} fallback={t.notRegistered} />
                        <DetailTile label={t.trialContract} value={generatedContract?.contractNumber || (result.contractCreated ? t.generated : t.notGenerated)} fallback={t.notRegistered} />
                        <DetailTile label={t.cardHolder} value={cardToken.cardHolderName || payPhone.cardHolderName || t.notRegistered} fallback={t.notRegistered} />
                        <DetailTile label={t.document} value={cardToken.document || payPhone.document || t.notRegistered} fallback={t.notRegistered} />
                        <DetailTile label={t.card} value={cardToken.maskedCard || cardToken.cardBrand || t.tokenSaved} fallback={t.notRegistered} />
                        <DetailTile label={t.reference} value={payPhone.clientTransactionId || cardToken.validationClientTransactionId} fallback={t.notRegistered} />
                    </div>

                    <Button
                        type="primary"
                        size="large"
                        icon={<LoginOutlined />}
                        className="bm-payphone-primary-action"
                        onClick={() => window.location.assign(bluemedixPortalUrl)}
                    >
                        {t.goPortal}
                    </Button>

                    <p className="bm-payphone-footer">
                        {t.footer}
                        <br />
                        <strong>{t.protected}</strong>
                    </p>
                </div>
            </section>
        </PublicShell>
    );
};

const mapStateToProps = (_state: AppState) => ({});

export default connect(mapStateToProps, {
    confirmPublicPayPhoneTrial,
    confirmOrganizationCardTokenPayPhone
})(PayPhoneResponse);
